import { z } from 'zod'

const workingHoursSchema = z
  .object({
    sun: z
      .string()
      .regex(/^([01][0-9]|2[0-3]):[0-5][0-9]-([01][0-9]|2[0-3]):[0-5][0-9]$/),
    mon: z
      .string()
      .regex(/^([01][0-9]|2[0-3]):[0-5][0-9]-([01][0-9]|2[0-3]):[0-5][0-9]$/),
    tue: z
      .string()
      .regex(/^([01][0-9]|2[0-3]):[0-5][0-9]-([01][0-9]|2[0-3]):[0-5][0-9]$/),
    wed: z
      .string()
      .regex(/^([01][0-9]|2[0-3]):[0-5][0-9]-([01][0-9]|2[0-3]):[0-5][0-9]$/),
    thu: z
      .string()
      .regex(/^([01][0-9]|2[0-3]):[0-5][0-9]-([01][0-9]|2[0-3]):[0-5][0-9]$/),
    fri: z
      .string()
      .regex(/^([01][0-9]|2[0-3]):[0-5][0-9]-([01][0-9]|2[0-3]):[0-5][0-9]$/),
    sat: z
      .string()
      .regex(/^([01][0-9]|2[0-3]):[0-5][0-9]-([01][0-9]|2[0-3]):[0-5][0-9]$/),
  })
  .strict()

const optionalE164Schema = z
  .string()
  .regex(/^\+[1-9][0-9]{7,14}$/)
  .nullable()

export const reviewResponseSchema = z
  .object({
    schemaVersion: z.literal(1),
    status: z.enum(['REVIEW_REQUIRED', 'PROVISIONING']),
    revision: z.number().int().positive(),
    clinic: z
      .object({
        name: z.string().trim().min(2).max(120),
        address: z.string().trim().min(5).max(500),
        phone: z.string().trim().min(1).max(50),
        email: z.string().email().max(254),
        emergencyPhone: z.string().trim().min(1).max(50).nullable(),
        timeZone: z.string().trim().min(1).max(100),
        workingHours: workingHoursSchema,
      })
      .strict(),
    nexHealth: z
      .object({
        connected: z.boolean(),
        selectedLocationName: z.string().trim().min(1).max(200),
      })
      .strict(),
    scheduling: z
      .object({
        usableProviderCount: z.number().int().nonnegative().max(300),
        defaultProviderName: z.string().trim().min(1).max(200).nullable(),
        newAppointmentBooking: z.boolean(),
        appointmentTypes: z
          .array(
            z
              .object({
                name: z.string().trim().min(1).max(200),
                minutes: z.number().int().positive().max(1_440),
              })
              .strict()
          )
          .max(300),
        validationStatus: z.enum([
          'VALID_QUERY_NO_SLOTS',
          'VALID_QUERY_WITH_SLOTS',
          'PMS_INTENTIONALLY_DISABLED',
        ]),
      })
      .strict(),
    communications: z
      .object({
        sms: z.boolean(),
        voice: z.boolean(),
        areaCode: z
          .string()
          .regex(/^[2-9][0-9]{2}$/)
          .nullable(),
        forwardingNumber: optionalE164Schema,
        frontDeskFirst: z.boolean(),
        ringCountBeforeAI: z.number().int().min(2).max(8),
        assignedPublicNumber: optionalE164Schema,
        setupStatus: z.enum([
          'NOT_STARTED',
          'IN_PROGRESS',
          'COMPLETED',
          'NEEDS_ATTENTION',
        ]),
      })
      .strict(),
  })
  .strict()
  .superRefine((value, ctx) => {
    const disabled = value.scheduling.validationStatus === 'PMS_INTENTIONALLY_DISABLED'
    const valid = disabled
      ? !value.nexHealth.connected && !value.scheduling.newAppointmentBooking && value.scheduling.usableProviderCount === 0 && value.scheduling.appointmentTypes.length === 0 && value.scheduling.defaultProviderName === null
      : value.nexHealth.connected && value.scheduling.usableProviderCount > 0 && value.scheduling.appointmentTypes.length > 0
    if (!valid) ctx.addIssue({code:z.ZodIssueCode.custom,message:'Scheduling capability is inconsistent'})
  })

export type ReviewResponse = z.infer<typeof reviewResponseSchema>
